'use client';

import type { ComponentProps } from 'react';
import { cn } from '@ifixit/ui/lib/utils';
import { DIALOG_BACKDROP } from './dialog';
import {
   ModalClose,
   ModalContent,
   ModalDescription,
   ModalPortal,
   ModalRoot,
   ModalTitle,
   ModalTrigger,
} from './modal-dialog';

export function AlertDialog(props: Omit<ComponentProps<typeof ModalRoot>, 'role' | 'modal'>) {
   return <ModalRoot role="alertdialog" {...props} />;
}

export function AlertDialogTrigger(props: ComponentProps<typeof ModalTrigger>) {
   return <ModalTrigger data-slot="alert-dialog-trigger" {...props} />;
}

export function AlertDialogPortal(props: ComponentProps<typeof ModalPortal>) {
   return <ModalPortal {...props} />;
}

/**
 * Asks before something that can't be undone. A press outside doesn't close
 * it, and it opens on Cancel, the safe choice.
 */
export function AlertDialogContent({
   className,
   onOpenAutoFocus,
   ...props
}: Omit<ComponentProps<typeof ModalContent>, 'onInteractOutside'>) {
   return (
      <AlertDialogPortal>
         <ModalContent
            data-slot="alert-dialog-content"
            onInteractOutside={event => event.preventDefault()}
            onOpenAutoFocus={event => {
               onOpenAutoFocus?.(event);
               if (event.defaultPrevented) return;
               event.preventDefault();
               const dialog = event.currentTarget as HTMLDialogElement;
               dialog
                  .querySelector<HTMLElement>('[data-slot="alert-dialog-cancel"]')
                  ?.focus({ preventScroll: true });
            }}
            className={cn(
               DIALOG_BACKDROP,
               'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-dialog grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-2xl border p-6 shadow-lg duration-200 sm:max-w-lg',
               className
            )}
            {...props}
         />
      </AlertDialogPortal>
   );
}

export function AlertDialogHeader({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         data-slot="alert-dialog-header"
         className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
         {...props}
      />
   );
}

export function AlertDialogFooter({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         data-slot="alert-dialog-footer"
         className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
         {...props}
      />
   );
}

export function AlertDialogTitle({ className, ...props }: ComponentProps<typeof ModalTitle>) {
   return (
      <ModalTitle
         data-slot="alert-dialog-title"
         className={cn('text-lg leading-none font-semibold', className)}
         {...props}
      />
   );
}

export function AlertDialogDescription({
   className,
   ...props
}: ComponentProps<typeof ModalDescription>) {
   return (
      <ModalDescription
         data-slot="alert-dialog-description"
         className={cn('text-muted-foreground text-sm', className)}
         {...props}
      />
   );
}

export function AlertDialogAction({ className, ...props }: ComponentProps<typeof ModalClose>) {
   return (
      <ModalClose
         data-slot="alert-dialog-action"
         className={cn('button-primary button-md', className)}
         {...props}
      />
   );
}

export function AlertDialogCancel({ className, ...props }: ComponentProps<typeof ModalClose>) {
   return (
      <ModalClose
         data-slot="alert-dialog-cancel"
         className={cn('button-outline button-md', className)}
         {...props}
      />
   );
}
